import React from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, ToggleButton, ToggleButtonGroup, Select, MenuItem, Typography,
} from '@mui/material';

// ---------------------------------------------------------------------------
// CategoricalGrid — shared, mobile-first editable grid for MSA attribute entry.
//
// The rating cells are categorical (Pass/Fail toggle or a dropdown) — never free
// text — which is the primary requirement for attribute data entry. It is column-
// schema driven so both the Attribute Agreement and Attribute Analytic modules can
// reuse it with different row shapes.
//
// Props
// -----
// columns : Array<{
//     key: string,                     // row-object key this column reads/writes
//     label: string,                   // header text
//     type: 'readonly' | 'number' | 'category',
//     categories?: string[],           // for type:'category' — overrides the grid default
//     width?: number|string,           // optional fixed column width
//     align?: 'left'|'center'|'right',
//     placeholder?: string,            // for type:'number'
// }>
// rows        : Array<object>          // one plain object per row, keyed by column.key
// onCellChange: (rowIndex, key, value) => void
// categories  : string[]               // default category list for 'category' columns (default ['Pass','Fail'])
// disabled    : boolean
// maxHeight   : number                 // scroll container height (default 440)
// ---------------------------------------------------------------------------

// Semantic colours for common Pass/Fail-style labels; blue accent otherwise.
function categoryColor(label) {
    const s = String(label).toLowerCase();
    if (/(pass|accept|good|conform|ok|go\b)/.test(s)) return '#81c784';
    if (/(fail|reject|defect|bad|nonconform|no.?go|scrap)/.test(s)) return '#e57373';
    return '#90caf9';
}

const cellInputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255,255,255,0.9)',
        fontSize: '0.85rem',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144,202,249,0.7)' },
    },
};

function CategoryCell({ value, categories, onChange, disabled, ariaLabel }) {
    // Toggle buttons for a small category set (touch-friendly); dropdown beyond 3.
    if (categories.length <= 3) {
        return (
            <ToggleButtonGroup
                exclusive
                size="small"
                value={value ?? null}
                onChange={(_e, v) => { if (v !== null) onChange(v); }}
                disabled={disabled}
                aria-label={ariaLabel}
                sx={{ flexWrap: 'nowrap' }}
            >
                {categories.map((c) => {
                    const color = categoryColor(c);
                    return (
                        <ToggleButton
                            key={c}
                            value={c}
                            sx={{
                                px: 1.5, py: 0.4,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                color: 'rgba(255,255,255,0.6)',
                                borderColor: 'rgba(255,255,255,0.15)',
                                '&.Mui-selected': {
                                    color,
                                    backgroundColor: `${color}22`,
                                    borderColor: `${color}66`,
                                    '&:hover': { backgroundColor: `${color}33` },
                                },
                            }}
                        >
                            {c}
                        </ToggleButton>
                    );
                })}
            </ToggleButtonGroup>
        );
    }

    return (
        <Select
            size="small"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            displayEmpty
            aria-label={ariaLabel}
            inputProps={{ 'aria-label': ariaLabel }}
            sx={{
                minWidth: 96,
                color: 'rgba(255,255,255,0.9)',
                fontSize: '0.85rem',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.45)' },
                '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.6)' },
            }}
            MenuProps={{
                PaperProps: {
                    sx: {
                        backgroundColor: '#1e242c',
                        border: '1px solid rgba(255,255,255,0.12)',
                        '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' },
                    },
                },
            }}
        >
            <MenuItem value="" disabled sx={{ color: 'rgba(255,255,255,0.4)' }}>—</MenuItem>
            {categories.map((c) => (
                <MenuItem key={c} value={c} sx={{ color: categoryColor(c) }}>{c}</MenuItem>
            ))}
        </Select>
    );
}

const headCellSx = {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    backgroundColor: '#1a1f26',
    py: 1,
    px: 1.25,
    whiteSpace: 'nowrap',
};

const bodyCellSx = {
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    py: 0.5,
    px: 1.25,
};

function CategoricalGrid({
    columns,
    rows,
    onCellChange,
    categories = ['Pass', 'Fail'],
    disabled = false,
    maxHeight = 440,
}) {
    if (!Array.isArray(columns) || columns.length === 0) return null;

    return (
        <TableContainer sx={{ maxHeight, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.key} align={col.align || 'left'} sx={{ ...headCellSx, width: col.width }}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => {
                        // Accessible name for each editable cell = the readonly context
                        // cells (e.g. "P1 Op1 1"), so screen readers announce which cell.
                        const rowLabel = columns.filter((c) => c.type === 'readonly').map((c) => row[c.key]).filter((v) => v != null).join(' ') || `Row ${rowIndex + 1}`;
                        return (
                        <TableRow key={rowIndex} hover sx={{ '&:hover': { backgroundColor: 'rgba(144,202,249,0.04)' } }}>
                            {columns.map((col) => {
                                const value = row[col.key];
                                const align = col.align || 'left';

                                if (col.type === 'category') {
                                    return (
                                        <TableCell key={col.key} align={align} sx={bodyCellSx}>
                                            <CategoryCell
                                                value={value}
                                                categories={col.categories || categories}
                                                onChange={(v) => onCellChange(rowIndex, col.key, v)}
                                                disabled={disabled}
                                                ariaLabel={`${col.label} for ${rowLabel}`}
                                            />
                                        </TableCell>
                                    );
                                }

                                if (col.type === 'number') {
                                    return (
                                        <TableCell key={col.key} align={align} sx={bodyCellSx}>
                                            <TextField
                                                variant="outlined"
                                                size="small"
                                                value={value ?? ''}
                                                onChange={(e) => onCellChange(rowIndex, col.key, e.target.value)}
                                                disabled={disabled}
                                                placeholder={col.placeholder || '0.000'}
                                                inputProps={{ inputMode: 'decimal', 'aria-label': `${col.label} for ${rowLabel}`, style: { padding: '6px 8px', textAlign: align } }}
                                                sx={{ ...cellInputSx, width: col.width || 110 }}
                                            />
                                        </TableCell>
                                    );
                                }

                                // readonly
                                return (
                                    <TableCell key={col.key} align={align} sx={{ ...bodyCellSx, py: 0.75 }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                                            {value ?? '—'}
                                        </Typography>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default CategoricalGrid;
